import React, { useState, useRef, useCallback, useEffect } from "react";

// ✅ audioRef kommt direkt als Prop – kein AudioContext aus App.js nötig
export default function DJMixSection({ audioRef, isPlaying, audioContextRef, sourceNodeRef,limiterNodeRef }) {
  const [isOpen, setIsOpen] = useState(false);
  const [fxType, setFxType] = useState("delay");
  const [padPos, setPadPos] = useState({ x: 0.5, y: 0.5 });
  const [padActive, setPadActive] = useState(false);
  const [fxReady, setFxReady] = useState(false);
  const [fxEnabled, setFxEnabled] = useState(true);
  const [fxIntensity, setFxIntensity] = useState(1.5); // Multiplikator für die Lautstärke
  const [fxParam2, setFxParam2] = useState(0.7); // Max Feedback für Delay

  // Internal Audio Refs
  const ctxRef = useRef(null);
  const limiterRef = useRef(null);
  const delayRef = useRef(null);
  const delayFeedbackRef = useRef(null);
  const delayGainRef = useRef(null);
  const reverbRef = useRef(null);
  const reverbGainRef = useRef(null);
  const filterRef = useRef(null);
  const filterGainRef = useRef(null);
  const fxInitialized = useRef(false);
  const padRef = useRef(null);

  // ─── IMPULSE RESPONSE ────────────────────────────────────────
  const generateImpulse = (ctx, convolver, duration, decay) => {
    const rate = ctx.sampleRate;
    const length = Math.floor(rate * duration);
    const impulse = ctx.createBuffer(2, length, rate);
    for (let c = 0; c < 2; c++) {
      const channel = impulse.getChannelData(c);
      for (let i = 0; i < length; i++) {
        channel[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, decay);
      }
    }
    convolver.buffer = impulse;
  };

  // ─── INIT FX – called on user gesture when opening ───────────
  const initFX = useCallback(() => {
    if (fxInitialized.current) return true;

    // Wir greifen auf den Master-Context und die Source aus der App.js zu
    const ctx = audioContextRef.current;
    const source = sourceNodeRef.current;
    const masterLimiter = limiterNodeRef.current;

    if (!ctx || !source || !masterLimiter) {
      console.warn("Master Audio nicht bereit");
      return false;
    }

    try {
    
      // DELAY
      const delay = ctx.createDelay(5.0);
      delay.delayTime.value = 0.3;
      const delayFeedback = ctx.createGain();
      delayFeedback.gain.value = 0.3;
      const delayGain = ctx.createGain();
      delayGain.gain.value = 0;
      delay.connect(delayFeedback);
      delayFeedback.connect(delay);
      delay.connect(delayGain);
      delayRef.current = delay;
      delayFeedbackRef.current = delayFeedback;
      delayGainRef.current = delayGain;

      // REVERB
      const reverb = ctx.createConvolver();
      const reverbGain = ctx.createGain();
      reverbGain.gain.value = 0;
      generateImpulse(ctx, reverb, 2, 2);
      reverb.connect(reverbGain);
      reverbRef.current = reverb;
      reverbGainRef.current = reverbGain;

      // FILTER
      const filter = ctx.createBiquadFilter();
      filter.type = "lowpass";
      filter.frequency.value = 20000;
      const filterGain = ctx.createGain();
      filterGain.gain.value = 0;
      filter.connect(filterGain);
      filterRef.current = filter;
      filterGainRef.current = filterGain;

      // --- NEUE VERBINDUNGSLOGIK ---
    
      // Wir verbinden die Quelle zusätzlich mit den Effekten
      source.connect(delay);
      source.connect(reverb); // falls vorhanden
      source.connect(filter); // falls vorhanden

      // Die Ausgänge der Effekte gehen in den Master-Limiter der App.js
      delayGain.connect(masterLimiter);
      reverbGain.connect(masterLimiter);
      filterGain.connect(masterLimiter);

      fxInitialized.current = true;
      setFxReady(true);
      console.log("✅ DJ FX an Master-Chain angeschlossen");
      return true;
    } catch(e) {
      console.error("❌ DJ FX init failed:", e);
      return false;
    }
  }, [audioContextRef, sourceNodeRef, limiterNodeRef]);
  

  // ─── OPEN HANDLER – init on user gesture ─────────────────────
  const handleOpen = () => {
    const opening = !isOpen;
    setIsOpen(opening);
    if (opening && !fxInitialized.current) {
      // Small delay to let React render first
      setTimeout(() => {
        const success = initFX();
        if (!success) {
          console.warn("FX init failed – audio may not be loaded yet");
        }
      }, 50);
    }
    if (opening && ctxRef.current?.state === "suspended") {
      ctxRef.current.resume();
    }
  };

  // ─── APPLY FX ────────────────────────────────────────────────
  const applyFX = useCallback((x, y, active) => {
    const ctx = audioContextRef.current;
    if (!ctx || !fxInitialized.current || !fxEnabled) return;
    const now = ctx.currentTime;
  
    if (!active) {
      if (delayGainRef.current) delayGainRef.current.gain.setTargetAtTime(0, now, 0.3);
      if (reverbGainRef.current) reverbGainRef.current.gain.setTargetAtTime(0, now, 0.3);
      if (filterGainRef.current) filterGainRef.current.gain.setTargetAtTime(0, now, 0.3);
      return;
    }
  
    if (fxType === "delay") {
      if (delayRef.current)
        delayRef.current.delayTime.setTargetAtTime(x * 0.8 + 0.05, now, 0.05);
      if (delayFeedbackRef.current)
        // Nutzt fxParam2 für das maximale Feedback
        delayFeedbackRef.current.gain.setTargetAtTime(y * fxParam2, now, 0.05);
      if (delayGainRef.current)
        // Nutzt fxIntensity für die Lautstärke
        delayGainRef.current.gain.setTargetAtTime(y * fxIntensity, now, 0.05);
  
    } else if (fxType === "reverb") {
      if (reverbRef.current)
        // Nutzt fxParam2 für die Raumgröße (Decay)
        generateImpulse(ctx, reverbRef.current, x * (fxParam2 * 6) + 0.5, y * 4 + 0.5);
      if (reverbGainRef.current)
        // Reverb braucht oft mehr Gain, daher * 1.5
        reverbGainRef.current.gain.setTargetAtTime(y * fxIntensity * 1.5, now, 0.1);
  
    } else if (fxType === "filter") {
      if (filterRef.current) {
        filterRef.current.frequency.setTargetAtTime(80 + x * 19920, now, 0.05);
        // Nutzt fxParam2 für die Resonanz (Q-Faktor)
        filterRef.current.Q.setTargetAtTime(y * (fxParam2 * 30), now, 0.05);
      }
      if (filterGainRef.current)
        filterGainRef.current.gain.setTargetAtTime(fxIntensity * 0.8, now, 0.05);
    }
  }, [fxType, fxEnabled, audioContextRef, fxIntensity, fxParam2]);


  // ─── PAD POSITION ────────────────────────────────────────────
  const getPosFromEvent = (e) => {
    const el = padRef.current;
    if (!el) return { x: 0.5, y: 0.5 };
    const rect = el.getBoundingClientRect();
    const touch = e.touches ? e.touches[0] : e;
    return {
      x: Math.max(0, Math.min(1, (touch.clientX - rect.left) / rect.width)),
      y: Math.max(0, Math.min(1, 1 - (touch.clientY - rect.top) / rect.height)),
    };
  };

  // ─── PAD HANDLERS ────────────────────────────────────────────
  const handleStart = useCallback((e) => {
    e.preventDefault();
    if (ctxRef.current?.state === "suspended") ctxRef.current.resume();
    const pos = getPosFromEvent(e);
    setPadPos(pos);
    setPadActive(true);
    applyFX(pos.x, pos.y, true);
  }, [applyFX]);

  const handleMove = useCallback((e) => {
    e.preventDefault();
    if (!padActive) return;
    const pos = getPosFromEvent(e);
    setPadPos(pos);
    applyFX(pos.x, pos.y, true);
  }, [padActive, applyFX]);

  const handleEnd = useCallback((e) => {
    e?.preventDefault();
    setPadActive(false);
    applyFX(0.5, 0.5, false);
  }, [applyFX]);

  // ─── Attach touch events directly to DOM (fixes iOS) ─────────
  useEffect(() => {
    const el = padRef.current;
    if (!el) return;
    el.addEventListener("touchstart", handleStart, { passive: false });
    el.addEventListener("touchmove", handleMove, { passive: false });
    el.addEventListener("touchend", handleEnd, { passive: false });
    return () => {
      el.removeEventListener("touchstart", handleStart);
      el.removeEventListener("touchmove", handleMove);
      el.removeEventListener("touchend", handleEnd);
    };
  }, [handleStart, handleMove, handleEnd]);

  // ─── FX LABELS ───────────────────────────────────────────────
  const getFxLabels = (type) => {
    switch(type) {
      case "delay":  return { x: "DELAY TIME", y: "FEEDBACK" };
      case "reverb": return { x: "ROOM SIZE",  y: "WET MIX" };
      case "filter": return { x: "FREQUENCY",  y: "RESONANCE" };
      default:       return { x: "X", y: "Y" };
    }
  };
  const labels = getFxLabels(fxType);


  // ─── RENDER ──────────────────────────────────────────────────
  return (
    <div style={{ marginBottom: "20px", width: "100%" }}>

      {/* TOGGLE BUTTON */}
      <button
        onClick={handleOpen}
        style={{
          width: "100%", padding: "12px 15px",
          background: isOpen ? "rgba(0,172,193,0.08)" : "rgba(255,255,255,0.02)",
          border: isOpen ? "1px solid rgba(0,172,193,0.3)" : "1px solid #1a1a1a",
          borderRadius: isOpen ? "12px 12px 0 0" : "12px",
          color: isOpen ? "#00acc1" : "#444",
          fontWeight: "900", fontSize: "0.6rem",
          letterSpacing: "2px", cursor: "pointer",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}
      >
        <span>🎛 DJ FX MIXER</span>
        <span style={{ fontSize: "0.5rem" }}>{isOpen ? "▲ CLOSE" : "▼ OPEN"}</span>
      </button>

      {isOpen && (
        <div style={{
          background: "rgba(0,0,0,0.9)", border: "1px solid #1a1a1a",
          borderTop: "none", borderRadius: "0 0 12px 12px", padding: "15px",
        }}>

          {/* STATUS BAR */}
          <div style={{ marginBottom: "12px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: "0.55rem", color: fxReady ? "#00acc1" : "#ff9800", letterSpacing: "1px" }}>
              {fxReady ? "✅ FX READY" : "⏳ LOADING..."}
            </span>
            <button
              onClick={() => {
                setFxEnabled(!fxEnabled);
                if (fxEnabled) applyFX(0.5, 0.5, false);
              }}
              style={{
                background: fxEnabled ? "rgba(0,172,193,0.15)" : "none",
                border: `1px solid ${fxEnabled ? "#00acc1" : "#333"}`,
                color: fxEnabled ? "#00acc1" : "#444",
                padding: "3px 10px", borderRadius: "6px",
                fontSize: "0.55rem", fontWeight: "900", cursor: "pointer"
              }}
            >
              {fxEnabled ? "FX ON" : "FX OFF"}
            </button>
          </div>

          {/* KAOSS PAD */}
          <div
            ref={padRef}
            onMouseDown={handleStart}
            onMouseMove={handleMove}
            onMouseUp={handleEnd}
            onMouseLeave={handleEnd}
            style={{
              position: "relative",
              width: "100%",
              paddingBottom: "75%",
              background: padActive
                ? "rgba(0,172,193,0.1)"
                : "rgba(0,0,0,0.7)",
              border: padActive ? "1px solid #00acc1" : "1px solid #222",
              borderRadius: "12px",
              cursor: "crosshair",
              overflow: "hidden",
              touchAction: "none",
              userSelect: "none",
              marginBottom: "10px",
              transition: "border-color 0.1s, background 0.1s",
            }}
          >
            {/* Grid lines */}
            {[25, 50, 75].map(pct => (
              <React.Fragment key={pct}>
                <div style={{ position: "absolute", left: `${pct}%`, top: 0, bottom: 0, width: "1px", background: "rgba(255,255,255,0.04)" }} />
                <div style={{ position: "absolute", top: `${pct}%`, left: 0, right: 0, height: "1px", background: "rgba(255,255,255,0.04)" }} />
              </React.Fragment>
            ))}

            {/* FX Type label */}
            <div style={{ position: "absolute", top: "8px", left: "10px", fontSize: "0.6rem", color: padActive ? "#00acc1" : "#333", fontWeight: "900", letterSpacing: "2px", pointerEvents: "none" }}>
              {fxType.toUpperCase()}
            </div>

            {/* Axis hints */}
            <div style={{ position: "absolute", bottom: "8px", left: "10px", fontSize: "0.5rem", color: "#222", pointerEvents: "none" }}>
              ← {labels.x} →
            </div>
            <div style={{ position: "absolute", top: "8px", right: "10px", fontSize: "0.5rem", color: "#222", pointerEvents: "none" }}>
              ↑ {labels.y}
            </div>

            {/* Crosshair dot – moves with finger */}
            {padActive && (
              <>
                <div style={{ position: "absolute", top: `${(1 - padPos.y) * 100}%`, left: 0, right: 0, height: "1px", background: "rgba(0,172,193,0.4)", pointerEvents: "none" }} />
                <div style={{ position: "absolute", left: `${padPos.x * 100}%`, top: 0, bottom: 0, width: "1px", background: "rgba(0,172,193,0.4)", pointerEvents: "none" }} />
                <div style={{
                  position: "absolute",
                  left: `${padPos.x * 100}%`,
                  top: `${(1 - padPos.y) * 100}%`,
                  width: "18px", height: "18px",
                  borderRadius: "50%",
                  background: "#00acc1",
                  transform: "translate(-50%, -50%)",
                  boxShadow: "0 0 20px #00acc1, 0 0 40px rgba(0,172,193,0.3)",
                  pointerEvents: "none",
                }} />
              </>
            )}

            {/* Center hint */}
            {!padActive && (
              <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", fontSize: "0.6rem", color: "#222", letterSpacing: "2px", pointerEvents: "none", textAlign: "center" }}>
                {fxReady ? "TOUCH & DRAG" : "⏳"}
              </div>
            )}
          </div>

          {/* FX SELECTOR BUTTONS */}
          <div style={{ display: "flex", gap: "6px" }}>
            {[
              { id: "delay", label: "DELAY" },
              { id: "reverb", label: "REVERB" },
              { id: "filter", label: "FILTER" },
            ].map(fx => (
              <button
                key={fx.id}
                onClick={() => {
                  applyFX(0.5, 0.5, false); // fade out current
                  setFxType(fx.id);
                }}
                style={{
                  flex: 1, padding: "8px 4px",
                  background: fxType === fx.id ? "rgba(0,172,193,0.15)" : "rgba(255,255,255,0.02)",
                  border: fxType === fx.id ? "1px solid #00acc1" : "1px solid #222",
                  borderRadius: "8px",
                  color: fxType === fx.id ? "#00acc1" : "#555",
                  fontSize: "0.55rem", fontWeight: "900",
                  letterSpacing: "1px", cursor: "pointer",
                }}
              >
                {fx.label}
              </button>
            ))}
          </div>

          {/* MASTER SLIDER SECTION */}
          <div style={{ padding: "12px 0", display: "flex", flexDirection: "column", gap: "17px" }}>
  
            {/* INTENSITY SLIDER */}
            <div style={{ display: "flex", flexDirection: "column" }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.55rem", color: "#666", marginBottom: "7px", fontWeight: "bold" }}>
                <span>{fxType.toUpperCase()} INTENSITY</span>
                <span style={{ color: "#00acc1" }}>{Math.round(fxIntensity * 100)}%</span>
              </div>
              <input 
                type="range" min="0.1" max="4.0" step="0.1" 
                value={fxIntensity} 
                onChange={(e) => setFxIntensity(parseFloat(e.target.value))}
                style={{ accentColor: "#00acc1", cursor: "pointer", height: "4px" }}
              />
            </div>

            {/* DYNAMIC PARAMETER SLIDER */}
            <div style={{ display: "flex", flexDirection: "column" }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.55rem", color: "#666", marginBottom: "7px", fontWeight: "bold" }}>
                <span>{fxType === "delay" ? "MAX FEEDBACK" : fxType === "reverb" ? "MAX ROOM SIZE" : "RESONANCE STRENGTH"}</span>
                <span style={{ color: "#00acc1" }}>{Math.round(fxParam2 * 100)}%</span>
              </div>
              <input 
                type="range" min="0" max={fxType === "delay" ? "0.95" : "1.5"} step="0.05" 
                value={fxParam2} 
                onChange={(e) => setFxParam2(parseFloat(e.target.value))}
                style={{ accentColor: "#00acc1", cursor: "pointer", height: "4px" }}
              />
            </div>
          </div>


        </div>
      )}
    </div>
  );
}